import {
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
} from "nsitools-react";
import React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import { SectionDto, SectionsApi } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import {
  SmallFormGenerator,
  FGSRSHCheckboxInput,
} from "../../../../components";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { SectionLinkedActivitiesPage } from "./SectionLinkedActivitiesPage";

export interface ISectionDetailPageProps {
  sectionId?: number;
  edit?: boolean;
}

export const SectionDetailPage: React.FunctionComponent<ISectionDetailPageProps> = ({
  sectionId,
  edit = false,
}) => {
  const { t } = useTl();
  const api = useApiService(SectionsApi);
  const history = useHistory();
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      code: Yup.string()
        .nullable()
        .required(t(ETLCodes.Required))
        .test(
          "is-unique",
          t(ETLCodes.CodeAlreadyExists),
          async function (value) {
            if (!value) return true;
            const res = await api.isSectionUnique(value, sectionId);
            return res.value;
          }
        ),
    });
  }, [api, sectionId, t]);

  const {
    data,
    loading,
    deleteItem,
    deleting,
    saveItem,
    saving,
  } = useCrudApi<SectionDto>(
    {
      getApiFn: (id = sectionId) =>
        id > 0 ? api.getSection(id) : new SectionDto().toJSON(),
      saveApiFn: (d) => api.saveSection(d),
      onSavedRoute: (d) => `${ERoutes.section}/${d.sectionId}/detail/edit`,
      deleteApiFn: (d) => api.deleteSection(d.sectionId),
      onDeletedRoute: () => ERoutes.section,
    },
    [sectionId]
  );

  return (
    <div>
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={edit}
        validationSchema={FormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.section)}
        onDelete={deleteItem}
        showDeleteButton={sectionId > 0}
        saving={saving}
        deleting={deleting}
      >
        <FieldGroup>
          <FGTextInput label={t(ETLCodes.Code)} name="code" />
          <FGTextAreaInput label={t(ETLCodes.Remark)} name="remark" />
          <FGSRSHCheckboxInput name="isBeauty" label={t(ETLCodes.IsBeauty)} />
          <FGSRSHCheckboxInput name="isWork" label={t(ETLCodes.IsWork)} />
        </FieldGroup>
      </SmallFormGenerator>
      <FieldSet title={t(ETLCodes.Activities)} collapsable={true}>
        <SectionLinkedActivitiesPage sectionId={sectionId} />
      </FieldSet>
    </div>
  );
};
